import {
  CrmOpportunity,
  CrmPipelineStage,
  FunnelStage,
  OpportunityPriority,
  OpportunityStatus,
  TeamMember
} from '../types';
import { CRM_PIPELINE_STAGES, getCrmStageForStatus } from './crmPipeline';

type ImportField =
  | 'id'
  | 'title'
  | 'companyName'
  | 'contactName'
  | 'contactEmail'
  | 'contactPhone'
  | 'stage'
  | 'value'
  | 'probability'
  | 'status'
  | 'priority'
  | 'assignee'
  | 'expectedCloseDate'
  | 'tags'
  | 'notes';

export interface CrmImportError {
  row: number;
  message: string;
}

export interface CrmImportResult {
  opportunities: CrmOpportunity[];
  errors: CrmImportError[];
  missingRequiredHeaders: string[];
}

export interface CrmImportContext {
  /** @deprecated Kept only so older callers can import without losing compatibility. */
  stagesOrder?: Array<{ stage: FunnelStage; label: string }>;
  teamMembers: TeamMember[];
  existingIds?: string[];
}

export const CRM_IMPORT_REQUIRED_HEADERS = ['Oportunidad', 'Contacto', 'Etapa'];

export const CRM_IMPORT_OPTIONAL_HEADERS = [
  'ID',
  'Empresa',
  'Email',
  'Teléfono',
  'Valor USD',
  'Probabilidad %',
  'Estado',
  'Prioridad',
  'Responsable',
  'Fecha Prevista',
  'Tags',
  'Notas'
];

export const CRM_IMPORT_TEMPLATE_HEADERS = [
  'Oportunidad',
  'Empresa',
  'Contacto',
  'Email',
  'Teléfono',
  'Etapa',
  'Valor USD',
  'Probabilidad %',
  'Estado',
  'Prioridad',
  'Responsable',
  'Fecha Prevista',
  'Tags',
  'Notas'
];

const HEADER_ALIASES: Record<ImportField, string[]> = {
  id: ['id', 'identificador'],
  title: ['oportunidad', 'nombre oportunidad', 'trato', 'titulo', 'title'],
  companyName: ['empresa', 'institucion', 'company', 'company name'],
  contactName: ['contacto', 'nombre contacto', 'contact name'],
  contactEmail: ['email', 'correo', 'correo electronico', 'contact email'],
  contactPhone: ['telefono', 'whatsapp', 'telefono whatsapp', 'phone'],
  stage: ['etapa', 'etapa del embudo', 'stage'],
  value: ['valor', 'valor usd', 'monto', 'value'],
  probability: ['probabilidad', 'probabilidad porcentaje', 'probability'],
  status: ['estado', 'status'],
  priority: ['prioridad', 'priority'],
  assignee: ['responsable', 'asignado', 'assignee'],
  expectedCloseDate: ['fecha prevista', 'fecha cierre', 'fecha estimada de cierre', 'expected close date'],
  tags: ['tags', 'etiquetas'],
  notes: ['notas', 'observaciones', 'notes']
};

const REQUIRED_FIELDS: Array<{ field: ImportField; header: string }> = [
  { field: 'title', header: 'Oportunidad' },
  { field: 'contactName', header: 'Contacto' },
  { field: 'stage', header: 'Etapa' }
];

const normalizeText = (value: unknown) => String(value ?? '')
  .trim()
  .toLocaleLowerCase('es')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/[^a-z0-9]+/g, ' ')
  .trim();

const cellText = (value: unknown) => value instanceof Date
  ? formatDate(value)
  : String(value ?? '').trim();

const formatDate = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const parseFlexibleNumber = (value: unknown): number | null => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const raw = cellText(value).replace(/[%$\sA-Za-z]/g, '');
  if (!raw) return null;

  let normalized = raw;
  if (raw.includes(',') && raw.includes('.')) {
    const decimalSeparator = raw.lastIndexOf(',') > raw.lastIndexOf('.') ? ',' : '.';
    const thousandsSeparator = decimalSeparator === ',' ? '.' : ',';
    normalized = raw.split(thousandsSeparator).join('').replace(decimalSeparator, '.');
  } else if (/^-?\d{1,3}([.,]\d{3})+$/.test(raw)) {
    normalized = raw.replace(/[.,]/g, '');
  } else {
    normalized = raw.replace(',', '.');
  }

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

const parseDate = (value: unknown): string | undefined | null => {
  if (value === null || value === undefined || value === '') return undefined;
  if (value instanceof Date && !Number.isNaN(value.getTime())) return formatDate(value);

  const raw = cellText(value);
  const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoMatch) return raw;

  const localMatch = raw.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (localMatch) {
    return `${localMatch[3]}-${localMatch[2].padStart(2, '0')}-${localMatch[1].padStart(2, '0')}`;
  }

  return null;
};

const CRM_STAGE_ALIASES: Record<CrmPipelineStage, string[]> = {
  lead: ['lead', 'prospecto', 'nuevo'],
  contacted: ['contacted', 'contactado', 'contactada', 'primer contacto'],
  qualified: ['qualified', 'calificado', 'calificada'],
  proposal: ['proposal', 'propuesta', 'propuesta enviada'],
  negotiation: ['negotiation', 'negociacion', 'negociando'],
  won: ['won', 'ganado', 'ganada', 'cerrado ganado'],
  lost: ['lost', 'perdido', 'perdida', 'descartado', 'descartada']
};

const parseStage = (value: unknown): CrmPipelineStage | null => {
  const raw = cellText(value);
  const numericStage = raw.match(/^\s*(?:etapa\s*)?(\d)\s*$/i);
  if (numericStage) return CRM_PIPELINE_STAGES[Number(numericStage[1]) - 1]?.stage || null;

  const normalized = normalizeText(raw);
  return CRM_PIPELINE_STAGES.find((item, index) => {
    return normalized === normalizeText(item.stage)
      || normalized === normalizeText(item.label)
      || CRM_STAGE_ALIASES[item.stage].some(alias => normalized === normalizeText(alias))
      || normalized === `etapa ${index + 1}`;
  })?.stage || null;
};

const parseStatus = (value: unknown): OpportunityStatus | null => {
  const normalized = normalizeText(value);
  if (!normalized) return 'open';
  if (['open', 'abierta', 'abierto', 'en curso'].includes(normalized)) return 'open';
  if (['won', 'ganada', 'ganado', 'cerrada', 'cerrado'].includes(normalized)) return 'won';
  if (['lost', 'perdida', 'perdido', 'descartada', 'descartado'].includes(normalized)) return 'lost';
  return null;
};

const parsePriority = (value: unknown): OpportunityPriority | null => {
  const normalized = normalizeText(value);
  if (!normalized) return 'medium';
  if (['low', 'baja', 'bajo'].includes(normalized)) return 'low';
  if (['medium', 'media', 'medio'].includes(normalized)) return 'medium';
  if (['high', 'alta', 'alto'].includes(normalized)) return 'high';
  if (['urgent', 'urgente'].includes(normalized)) return 'urgent';
  return null;
};

const findHeaderIndexes = (headerRow: unknown[]) => {
  const normalizedHeaders = headerRow.map(normalizeText);
  return Object.fromEntries(
    (Object.keys(HEADER_ALIASES) as ImportField[]).map(field => [
      field,
      normalizedHeaders.findIndex(header => HEADER_ALIASES[field].some(alias => normalizeText(alias) === header))
    ])
  ) as Record<ImportField, number>;
};

export const parseCsvText = (source: string): unknown[][] => {
  const text = source.replace(/^\uFEFF/, '');
  let delimiter = ',';
  let commaCount = 0;
  let semicolonCount = 0;
  let quoted = false;

  for (const char of text.split(/\r?\n/, 1)[0] || '') {
    if (char === '"') quoted = !quoted;
    if (!quoted && char === ',') commaCount += 1;
    if (!quoted && char === ';') semicolonCount += 1;
  }
  if (semicolonCount > commaCount) delimiter = ';';

  const rows: unknown[][] = [];
  let row: string[] = [];
  let cell = '';
  let inQuotes = false;

  for (let index = 0; index < text.length; index += 1) {
    const char = text[index];
    const next = text[index + 1];

    if (char === '"') {
      if (inQuotes && next === '"') {
        cell += '"';
        index += 1;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === delimiter && !inQuotes) {
      row.push(cell);
      cell = '';
    } else if ((char === '\n' || char === '\r') && !inQuotes) {
      if (char === '\r' && next === '\n') index += 1;
      row.push(cell);
      rows.push(row);
      row = [];
      cell = '';
    } else {
      cell += char;
    }
  }

  row.push(cell);
  rows.push(row);
  return rows;
};

export const parseCrmImportRows = (
  sourceRows: unknown[][],
  context: CrmImportContext
): CrmImportResult => {
  const rows = sourceRows.filter(row => row.some(cell => cellText(cell) !== ''));
  if (rows.length === 0) {
    return { opportunities: [], errors: [{ row: 1, message: 'El archivo está vacío.' }], missingRequiredHeaders: CRM_IMPORT_REQUIRED_HEADERS };
  }

  const indexes = findHeaderIndexes(rows[0]);
  const missingRequiredHeaders = REQUIRED_FIELDS
    .filter(required => indexes[required.field] < 0)
    .map(required => required.header);

  if (missingRequiredHeaders.length > 0) {
    return { opportunities: [], errors: [], missingRequiredHeaders };
  }

  const errors: CrmImportError[] = [];
  const opportunities: CrmOpportunity[] = [];
  const knownIds = new Set(context.existingIds || []);
  const today = new Date().toISOString().split('T')[0];
  const getCell = (row: unknown[], field: ImportField) => indexes[field] >= 0 ? row[indexes[field]] : undefined;

  rows.slice(1).forEach((row, rowIndex) => {
    const spreadsheetRow = rowIndex + 2;
    const rowErrors: string[] = [];
    const title = cellText(getCell(row, 'title'));
    const contactName = cellText(getCell(row, 'contactName'));
    const crmStage = parseStage(getCell(row, 'stage'));
    const rawValue = getCell(row, 'value');
    const parsedValue = rawValue === undefined || cellText(rawValue) === '' ? 0 : parseFlexibleNumber(rawValue);
    const rawProbability = getCell(row, 'probability');
    const parsedProbability = rawProbability === undefined || cellText(rawProbability) === '' ? 50 : parseFlexibleNumber(rawProbability);
    const status = parseStatus(getCell(row, 'status'));
    const priority = parsePriority(getCell(row, 'priority'));
    const expectedCloseDate = parseDate(getCell(row, 'expectedCloseDate'));
    const contactEmail = cellText(getCell(row, 'contactEmail'));
    const assigneeText = cellText(getCell(row, 'assignee'));
    const assignedMember = assigneeText
      ? context.teamMembers.find(member => (
        normalizeText(member.name) === normalizeText(assigneeText)
        || normalizeText(member.email) === normalizeText(assigneeText)
      ))
      : undefined;

    if (!title) rowErrors.push('falta Oportunidad');
    if (!contactName) rowErrors.push('falta Contacto');
    if (!crmStage) rowErrors.push('Etapa inválida (usa 1–7 o una etapa comercial válida)');
    if (parsedValue === null || parsedValue < 0) rowErrors.push('Valor USD inválido');
    if (parsedProbability === null || parsedProbability < 0 || parsedProbability > 100) rowErrors.push('Probabilidad inválida (0–100)');
    if (!status) rowErrors.push('Estado inválido (Abierta, Ganada o Perdida)');
    if (!priority) rowErrors.push('Prioridad inválida (Baja, Media, Alta o Urgente)');
    if (expectedCloseDate === null) rowErrors.push('Fecha Prevista inválida (AAAA-MM-DD o DD/MM/AAAA)');
    if (contactEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactEmail)) rowErrors.push('Email inválido');
    if (assigneeText && !assignedMember) rowErrors.push('Responsable no coincide con un nombre o email del equipo');

    if (rowErrors.length > 0 || !crmStage || parsedValue === null || parsedProbability === null || !status || !priority) {
      errors.push({ row: spreadsheetRow, message: rowErrors.join('; ') });
      return;
    }

    const sourceId = cellText(getCell(row, 'id'));
    let id = sourceId || `opp-import-${Date.now()}-${spreadsheetRow}`;
    if (knownIds.has(id)) id = `opp-import-${Date.now()}-${spreadsheetRow}`;
    knownIds.add(id);

    opportunities.push({
      id,
      title,
      contactName,
      contactEmail,
      contactPhone: cellText(getCell(row, 'contactPhone')) || undefined,
      companyName: cellText(getCell(row, 'companyName')) || undefined,
      stage: 'attraction',
      crmStage: getCrmStageForStatus(status, crmStage),
      value: parsedValue,
      probability: parsedProbability,
      status,
      priority,
      assignedMemberId: assignedMember?.id,
      expectedCloseDate: expectedCloseDate || undefined,
      notes: cellText(getCell(row, 'notes')) || undefined,
      tags: cellText(getCell(row, 'tags'))
        ? cellText(getCell(row, 'tags')).split(/[,;|]/).map(tag => tag.trim()).filter(Boolean)
        : [],
      createdAt: today,
      updatedAt: today,
      activities: [{
        id: `act-import-${Date.now()}-${spreadsheetRow}`,
        type: 'note',
        description: 'Importado desde CSV/Excel',
        timestamp: new Date().toISOString().replace('T', ' ').slice(0, 16)
      }]
    });
  });

  return { opportunities, errors, missingRequiredHeaders: [] };
};
